/**
 * PaymentForm Component
 *
 * Reusable Stripe payment form using Stripe Elements.
 * Handles card input, validation, and payment confirmation.
 */

import React, { useState } from 'react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import type { Appearance } from '@stripe/stripe-js';
import { Loader2 } from 'lucide-react';
import { getStripe } from '../../services/stripe';
import { Button } from '@/components/ui/button';

/**
 * Per-theme Stripe Elements appearance.
 *
 * The PaymentElement renders inside a Stripe-hosted iframe, so the CSS
 * tokens in src/index.css cannot reach it, and Stripe's appearance API
 * needs literal color values (hex, not oklch). The hex values below are
 * exact sRGB conversions of BOTH token blocks in src/index.css
 * (:root = dark, [data-theme='light'] = light). If a token changes
 * there, update the matching entry here.
 */
const STRIPE_APPEARANCE: Record<'dark' | 'light', Appearance> = {
  dark: {
    theme: 'night',
    variables: {
      colorPrimary: '#6c70ff', // --primary
      colorBackground: '#161822', // --popover
      colorText: '#f1f1f5', // --foreground
      colorTextSecondary: '#9597a5', // --muted-foreground
      colorDanger: '#f14445', // --destructive
      fontFamily: 'system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '10px', // --radius (rounded-lg)
    },
  },
  light: {
    theme: 'stripe',
    variables: {
      colorPrimary: '#5959e8', // --primary
      colorBackground: '#ffffff', // --popover
      colorText: '#161822', // --foreground
      colorTextSecondary: '#5a5d6c', // --muted-foreground
      colorDanger: '#d01c29', // --destructive
      fontFamily: 'system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '10px', // --radius (rounded-lg)
    },
  },
};

interface PaymentFormProps {
  clientSecret: string;
  onSuccess: () => void;
  onError: (error: string) => void;
  amount?: number;
  description?: string;
}

/**
 * Internal form component that uses Stripe hooks
 * Must be wrapped in Elements provider
 */
const PaymentFormContent: React.FC<PaymentFormProps> = ({
  clientSecret,
  onSuccess,
  onError,
  amount,
  description,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't loaded yet
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/app`,
        },
        redirect: 'if_required',
      });

      if (error) {
        // Payment failed
        const message = error.message || 'Payment failed. Please try again.';
        setErrorMessage(message);
        onError(message);
      } else {
        // Payment succeeded
        onSuccess();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setErrorMessage(message);
      onError(message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Amount and description */}
      {(amount || description) && (
        <div className="mb-6 text-center">
          {amount && (
            <div className="mb-2 text-2xl font-bold tracking-tight text-foreground tabular-nums">
              ${(amount / 100).toFixed(2)}
            </div>
          )}
          {description && <div className="text-sm text-muted-foreground">{description}</div>}
        </div>
      )}

      {/* Stripe Payment Element */}
      <div className="rounded-xl border border-border bg-card/80 p-4">
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {/* Error message */}
      {errorMessage && (
        <div className="rounded-xl border border-destructive/25 bg-destructive/10 p-3 text-sm text-destructive">
          {errorMessage}
        </div>
      )}

      {/* Submit button */}
      <Button type="submit" size="lg" disabled={!stripe || isProcessing} className="w-full">
        {isProcessing ? (
          <>
            <Loader2 data-icon="inline-start" className="animate-spin" />
            Processing…
          </>
        ) : (
          'Complete Payment'
        )}
      </Button>

      {/* Stripe badge */}
      <div className="text-center text-xs text-foreground/55">
        Secured by <span className="font-semibold text-foreground/70">Stripe</span>
      </div>
    </form>
  );
};

/**
 * PaymentForm Component
 *
 * Main component that wraps the form with Stripe Elements provider
 */
export const PaymentForm: React.FC<PaymentFormProps> = (props) => {
  const [stripePromise] = useState(() => getStripe());
  // Read the app theme once per mount: the payment modals mount this
  // component fresh each time they open, so a mid-session toggle while
  // the form is on screen is not a supported path.
  const [appearance] = useState<Appearance>(
    () =>
      STRIPE_APPEARANCE[
        document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark'
      ],
  );

  if (!props.clientSecret) {
    return (
      <div className="space-y-3 p-4" aria-label="Loading payment form">
        {/* Skeleton in the shape of the payment element */}
        <div className="h-10 animate-pulse rounded-lg bg-muted" />
        <div className="h-24 animate-pulse rounded-lg bg-muted" />
        <p className="text-center text-sm text-muted-foreground">Loading payment form…</p>
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: props.clientSecret,
        appearance,
      }}
    >
      <PaymentFormContent {...props} />
    </Elements>
  );
};
